import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Building2, Users, Shield, Zap, BarChart3, LayoutDashboard, ArrowRight, Target, Lightbulb, Workflow, Clock, DollarSign, Upload, FileText, Brain, Sparkles, TrendingUp, Bot, Mail, Twitter, Linkedin, Github } from 'lucide-react';

const EnterpriseHome = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const workflowSteps = [
    { icon: LayoutDashboard, text: "All clients in one view" },
    { icon: Sparkles, text: "Campaigns on autopilot" },
    { icon: TrendingUp, text: "Client-ready reports" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % workflowSteps.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [workflowSteps.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white overflow-hidden">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#c4ffa0]/20 text-[#c4ffa0] text-sm font-medium backdrop-blur-sm">
                <Brain className="w-4 h-4 mr-2" />
                AI-Native Marketing Operating System
            </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                One Platform. All Clients.{' '}
                <span className="bg-gradient-to-r from-[#c4ffa0] to-green-400 bg-clip-text text-transparent">
                  No Chaos.
                </span>
            </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Manage 10+ clients in one dashboard, run winning campaigns on autopilot, prove your value with client-ready reports, and scale personalization without adding headcount.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://calendly.com/kolanut"
                target="_blank"
                rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-[#c4ffa0] to-green-400 text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  Book a Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
                <a 
                  href="https://calendly.com/kolanut"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-[#c4ffa0]/30 text-[#c4ffa0] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#c4ffa0]/10 transition-all duration-300 backdrop-blur-sm"
                >
                  Get Early Access
                </a>
              </div>
            </div>

            {/* Right Column - Animated Dashboard Mockup */}
            <div className="relative">
              <div className="relative bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 shadow-2xl">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-white">Your command center</h3>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                  </div>
                  
                  {/* Workflow Steps */}
                  <div className="space-y-4">
                    {workflowSteps.map((step, index) => {
                      const Icon = step.icon;
                      return (
                        <div 
                          key={index}
                          className={`flex items-center space-x-4 p-4 rounded-xl transition-all duration-500 ${
                            activeStep === index 
                              ? 'bg-[#c4ffa0]/20 border border-[#c4ffa0]/30' 
                              : 'bg-gray-700/30 border border-gray-600/30'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                            activeStep === index 
                              ? 'bg-[#c4ffa0] text-[#2d4a1e]' 
                              : 'bg-gray-600 text-gray-300'
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className={`font-medium transition-all duration-300 ${
                            activeStep === index ? 'text-[#c4ffa0]' : 'text-gray-300'
                          }`}>
                            {step.text}
                          </span>
                          {activeStep === index && (
                            <div className="ml-auto">
                              <Sparkles className="w-5 h-5 text-[#c4ffa0] animate-pulse" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-[#c4ffa0] to-green-400 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${((activeStep + 1) / workflowSteps.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#c4ffa0]/20 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-green-400/20 rounded-full blur-xl animate-pulse delay-1000"></div>
            </div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#c4ffa0]/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-green-400/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-500/20 text-red-400 text-sm font-medium mb-6 backdrop-blur-sm">
              <Target className="w-4 h-4 mr-2" />
              The Problem
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Too many tools. Too many clients.{' '}
              <span className="text-red-400">Not enough proof of value.</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Juggling dashboards and spreadsheets across clients burns hours. Proving ROI and closing retainers gets harder when data is scattered — and scaling personalization usually means scaling headcount.
            </p>
          </div>
          
          {/* Split Screen Visual */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Before KinOS */}
            <div className="relative">
              <div className="bg-red-500/10 backdrop-blur-xl rounded-3xl p-8 border border-red-500/20">
                <h3 className="text-xl font-bold text-red-400 mb-6">Before Kolanut</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-red-500/10 rounded-xl">
                    <LayoutDashboard className="w-5 h-5 text-red-400" />
                    <span className="text-gray-300">Tool-switching across clients</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-red-500/10 rounded-xl">
                    <Clock className="w-5 h-5 text-red-400" />
                    <span className="text-gray-300">Manual campaign juggling</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-red-500/10 rounded-xl">
                    <DollarSign className="w-5 h-5 text-red-400" />
                    <span className="text-gray-300">Hard to prove ROI to clients</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-red-500/10 rounded-xl">
                    <Users className="w-5 h-5 text-red-400" />
                    <span className="text-gray-300">Personalization = more headcount</span>
                  </div>
                </div>
              </div>
            </div>

            {/* After Kolanut */}
            <div className="relative">
              <div className="bg-[#c4ffa0]/10 backdrop-blur-xl rounded-3xl p-8 border border-[#c4ffa0]/20">
                <h3 className="text-xl font-bold text-[#c4ffa0] mb-6">After Kolanut</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-[#c4ffa0]/10 rounded-xl">
                    <LayoutDashboard className="w-5 h-5 text-[#c4ffa0]" />
                    <span className="text-gray-300">One dashboard, all clients</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-[#c4ffa0]/10 rounded-xl">
                    <Zap className="w-5 h-5 text-[#c4ffa0]" />
                    <span className="text-gray-300">Campaigns on autopilot</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-[#c4ffa0]/10 rounded-xl">
                    <TrendingUp className="w-5 h-5 text-[#c4ffa0]" />
                    <span className="text-gray-300">Client-ready reports that prove value</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-[#c4ffa0]/10 rounded-xl">
                    <Bot className="w-5 h-5 text-[#c4ffa0]" />
                    <span className="text-gray-300">Scale personalization without headcount</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-32 h-32 bg-red-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-40 h-40 bg-[#c4ffa0]/5 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#c4ffa0]/20 text-[#c4ffa0] text-sm font-medium mb-6 backdrop-blur-sm">
              <Lightbulb className="w-4 h-4 mr-2" />
              The Solution
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Built for agencies and{' '}
              <span className="bg-gradient-to-r from-[#c4ffa0] to-green-400 bg-clip-text text-transparent">
                marketing consultants.
              </span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Manage 10+ Clients */}
            <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 hover:border-amber-400/30 transition-all duration-300 group">
              <div className="w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Manage 10+ Clients Without the Chaos</h3>
              <p className="text-gray-300 mb-4">
                One dashboard to rule them all – save hours weekly and eliminate tool-switching burnout.
              </p>
            </div>

            {/* Run Winning Campaigns */}
            <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 hover:border-indigo-400/30 transition-all duration-300 group">
              <div className="w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Run Winning Campaigns on Autopilot – For Every Client</h3>
              <p className="text-gray-300 mb-4">
                Maximize ROI and cut ad waste while you focus on strategy and winning new business.
              </p>
            </div>

            {/* Prove Your Value */}
            <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 hover:border-blue-400/30 transition-all duration-300 group">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Prove Your Value Instantly – Close & Retain More Retainers</h3>
              <p className="text-gray-300 mb-4">
                Turn data chaos into client-trusting dashboards and reports that justify every dollar.
              </p>
            </div>

            {/* Enterprise Personalization */}
            <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 hover:border-green-400/30 transition-all duration-300 group">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Bot className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Enterprise Personalization Without the Headcount</h3>
              <p className="text-gray-300 mb-4">
                Turn every client into a growth machine with AI that scales personalization effortlessly.
              </p>
            </div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-20 w-40 h-40 bg-[#c4ffa0]/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-green-400/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-500/20 text-green-400 text-sm font-medium mb-6 backdrop-blur-sm">
              <TrendingUp className="w-4 h-4 mr-2" />
              Results That Matter
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              From scattered tools to{' '}
              <span className="bg-gradient-to-r from-[#c4ffa0] to-green-400 bg-clip-text text-transparent">
                one platform that proves value.
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Agencies and consultants use Kolanut to run campaigns on autopilot, cut ad waste, and deliver client-ready reports — so you close more retainers and scale without adding headcount.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#c4ffa0] mb-2">3.2×</div>
              <div className="text-gray-300">ROAS on Autopilot Campaigns</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#c4ffa0] mb-2">22%</div>
              <div className="text-gray-300">Ad Waste Cut</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#c4ffa0] mb-2">4+ hrs</div>
              <div className="text-gray-300">Saved Per Week (No Tool-Switching)</div>
            </div>
          </div>

          <div className="text-center">
            <a 
              href="https://calendly.com/kolanut"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-gradient-to-r from-[#c4ffa0] to-green-400 text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Book a Demo – See Your Command Center
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
            </div>

        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-40 h-40 bg-green-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-32 h-32 bg-[#c4ffa0]/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
              </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Built for{' '}
              <span className="bg-gradient-to-r from-[#c4ffa0] to-green-400 bg-clip-text text-transparent">
                agencies and marketing consultants.
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              One dashboard, autopilot campaigns, client-ready reports, and enterprise personalization — all in one stack that works with Shopify, Klaviyo, Meta, Google, and more.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {['Creative & media agencies', 'Marketing consultants', 'Growth & performance teams', 'Brand & retention teams'].map((team, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 text-center hover:border-[#c4ffa0]/30 transition-all duration-300">
                <Users className="w-8 h-8 text-[#c4ffa0] mx-auto mb-3" />
                <p className="text-white font-medium">{team}</p>
              </div>
            ))}
            </div>

          {/* Integration Logos Grid */}
          <div className="bg-gray-800/30 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50">
            <h3 className="text-xl font-bold text-white text-center mb-8">Works with your stack</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-items-center">
              {[
                { name: 'Shopify', logo: '/integrations/shopify.webp' },
                { name: 'Klaviyo', logo: '/integrations/klaviyo.png' },
                { name: 'Meta', logo: '/integrations/meta.png' },
                { name: 'Google Ads', logo: '/integrations/google_ads.png', bigger: true },
                { name: 'Notion', logo: '/integrations/notion.png' },
                { name: 'Slack', logo: '/integrations/slack.png', bigger: true },
              ].map((tool, index) => (
                <div key={index} className="flex flex-col items-center justify-center gap-2">
                  <img
                    src={tool.logo}
                    alt={tool.name}
                    className={`h-12 w-auto max-w-[140px] object-contain opacity-80 hover:opacity-100 transition-opacity duration-300 md:h-14 md:max-w-[160px] ${tool.bigger ? '!h-16 !max-w-[180px] md:!h-20 md:!max-w-[220px]' : ''}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <span className="text-gray-400 font-medium text-sm opacity-80 hover:opacity-100 transition-opacity duration-300 hidden">
                    {tool.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-20 w-32 h-32 bg-[#c4ffa0]/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-green-400/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
      </section>

      {/* Why KinOS Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#c4ffa0]/20 text-[#c4ffa0] text-sm font-medium mb-6 backdrop-blur-sm">
              <Brain className="w-4 h-4 mr-2" />
              Why Kolanut
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              One platform. All clients.{' '}
              <span className="bg-gradient-to-r from-[#c4ffa0] to-green-400 bg-clip-text text-transparent">
                No chaos.
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Kolanut gives agencies and consultants one dashboard for every client, campaigns that run on autopilot, reports that prove value, and AI that scales personalization without more headcount.
            </p>
          </div>
          
          <div className="text-center">
            <a 
              href="https://calendly.com/kolanut"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-gradient-to-r from-[#c4ffa0] to-green-400 text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Book a Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#c4ffa0]/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-green-400/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
      </section>

      {/* Custom Enterprise Footer */}
      <footer className="relative bg-gradient-to-br from-gray-900 to-black border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12 lg:items-start">
            {/* Company Info */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-xl flex items-center justify-center">
                  <Brain className="w-6 h-6 text-[#2d4a1e]" />
                </div>
                <span className="text-2xl font-bold text-white">Kolanut AI</span>
              </div>
              <p className="text-gray-400 text-lg leading-relaxed mb-6 max-w-md">
                One dashboard for all clients. Campaigns on autopilot. Client-ready reports. Enterprise personalization without the headcount — for agencies and marketing consultants.
              </p>
              
              {/* Social Links */}
              <div className="flex space-x-4">
                <a href="mailto:hello@usekinos.com" className="w-10 h-10 bg-gray-800/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-[#c4ffa0] hover:bg-[#c4ffa0]/10 transition-all duration-300">
                  <Mail className="w-5 h-5" />
                </a>
                <a href="https://twitter.com/usekinos" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-[#c4ffa0] hover:bg-[#c4ffa0]/10 transition-all duration-300">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="https://linkedin.com/company/usekinos" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-[#c4ffa0] hover:bg-[#c4ffa0]/10 transition-all duration-300">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://github.com/usekinos" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-[#c4ffa0] hover:bg-[#c4ffa0]/10 transition-all duration-300">
                  <Github className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Support Links */}
            <div className="lg:justify-self-center">
              <h3 className="text-white font-semibold text-lg mb-6">Support</h3>
              <ul className="space-y-3 md:space-y-4">
                <li>
                  <a href="https://calendly.com/kolanut" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#c4ffa0] transition-colors duration-300">
                    Book a Demo
                  </a>
                </li>
                <li>
                  <a href="/blog" className="text-gray-400 hover:text-[#c4ffa0] transition-colors duration-300">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="/privacy-policy" className="text-gray-400 hover:text-[#c4ffa0] transition-colors duration-300">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms-of-use" className="text-gray-400 hover:text-[#c4ffa0] transition-colors duration-300">
                    Terms of Use
                  </a>
                </li>
                <li>
                  <a href="mailto:hello@usekinos.com" className="text-gray-400 hover:text-[#c4ffa0] transition-colors duration-300">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-800 mt-10 lg:mt-12 pt-6 md:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm text-center md:text-left">
                © 2026 Kolanut. All rights reserved.
              </p>
              <div className="flex items-center justify-center md:mt-0">
                <span className="text-gray-400 text-sm">Built with ❤️ for agencies</span>
              </div>
            </div>
          </div>
        </div>

        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-10 w-32 h-32 bg-[#c4ffa0]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-40 h-40 bg-green-400/5 rounded-full blur-3xl"></div>
        </div>
      </footer>
    </div>
  );
};

export default EnterpriseHome; 